import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Observable, tap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DEBOUNCE_TIME, DEFAULT_FILTER, DOCUMENTS_PAGE_SIZE } from './documents-page.config';
import { DocumentsFilter } from './documents-page.types';
import { DocumentsPageService } from './services/documents-page.service';
import { SharedModule } from '../../../shared/shared.module';
import { DocumentsFilterService } from './services/documents-filter.service';
import { DocumentTypesComponent } from '../document-types/document-types.component';
import { DocumentListComponent } from '../document-list/document-list.component';
import { PaginationService } from '../../../core/features/pagination-service/pagination.service';
import { DocumentModel } from '../../../core/modules/documents/documents.types';

@UntilDestroy()
@Component({
  selector: 'tax-documents-page',
  templateUrl: './documents-page.component.html',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    DocumentTypesComponent,
    DocumentListComponent,
  ],
})

export class DocumentsPageComponent implements OnInit, OnDestroy {
  kkmId = new FormControl<string | null>(null);
  countDisplayedDocuments = DOCUMENTS_PAGE_SIZE;
  readonly documents$: Observable<DocumentModel[]> = this.documentsPageService.getDocuments();
  readonly documentTypes$ = this.documentsPageService.getDocumentTypes();
  private documentsFilter = DEFAULT_FILTER;

  constructor(
    private documentsPageService: DocumentsPageService,
    private documentsFilterService: DocumentsFilterService,
    private paginationService: PaginationService,
  ) {}

  ngOnInit(): void {
    this.getDocumentsOnSearchInputChanges();
    this.paginationService.getPaginationState(DOCUMENTS_PAGE_SIZE)
      .pipe(
        tap((pagination) => { this.countDisplayedDocuments = pagination.pageSize; }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.documentsFilterService.resetDocumentsFilter();
    this.resetPaginationState();
  }

  setDocumentsFilter(value: Partial<DocumentsFilter>): void {
    this.documentsFilter = {
      ...this.documentsFilter, ...value,
    };
    this.documentsFilterService.setDocumentsFilter(this.documentsFilter);
    this.resetPaginationState();
  }

  changePageSize(): void {
    const pageSize = this.countDisplayedDocuments + DOCUMENTS_PAGE_SIZE;
    this.paginationService.setPaginationState({
      pageSize,
    });
  }

  private getDocumentsOnSearchInputChanges(): void {
    this.kkmId.valueChanges
      .pipe(
        debounceTime(DEBOUNCE_TIME),
        distinctUntilChanged(),
        untilDestroyed(this),
      ).subscribe(kkmId => this.setDocumentsFilter({
        kkmId,
      }));
  }

  private resetPaginationState(): void {
    this.paginationService.resetPaginationState(DOCUMENTS_PAGE_SIZE);
  }
}
